import React, {useCallback, useContext} from 'react';

import {FormInput} from 'src/components/input';
import {CRM_NAMES} from 'src/constants';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {INVALID_HUBSPOT_CRM_ID, VALIDATION_ERRORS} from 'src/modules/profile/account/create-user/constants';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import type {UserCrmModule} from 'src/openapi/admin/models/UserCrmModule';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    crmModule: UserCrmModule,
    onChange: (data: UserCrmModule) => void,
    disabled?: boolean,
    onFieldChange: (fieldName: string) => void,
};

const CRM_MAX_LENGTH = {
    [CrmTypes.SFCRM]: 18,
    [CrmTypes.MDCRM]: 36,
    [CrmTypes.DEALCLOUDCRM]: 100,
    [CrmTypes.HUBSPOTCRM]: 10,
    [CrmTypes.PITCHBOOK]: 0,
};

const MAX_INT_32 = 2147483647;

export const CrmSettingComponent = ({crmModule, onChange, disabled, onFieldChange}: Props) => {
    const {addError, removeError} = useContext(ValidationContext);

    const changeUserId = useCallback((crmId: string) => {
        onFieldChange(crmModule.crmType + ': User ID');
        onChange({...crmModule, crmId});

        if (crmModule.crmType === CrmTypes.HUBSPOTCRM && (isNaN(+crmId) || +crmId < 0 || +crmId > MAX_INT_32)) {
            addError({
                controlId: `user-${crmModule.crmType}-crm-id`,
                code: INVALID_HUBSPOT_CRM_ID,
                message: VALIDATION_ERRORS.INVALID_HUBSPOT_CRM_ID,
            });
        } else {
            removeError(`user-${crmModule.crmType}-crm-id`);
        }
    }, [crmModule, onChange, addError, removeError, onFieldChange]);

    const changeAdmin = useCallback((changeData: CheckBoxChangeData) => {
        onFieldChange(crmModule.crmType + ': isAdmin');
        onChange({
            ...crmModule,
            isAdmin: changeData.event.currentTarget.checked,
        });
    }, [crmModule, onChange, onFieldChange]);

    const changeContactsImport = useCallback((changeData: CheckBoxChangeData) => {
        onFieldChange(crmModule.crmType + ': isImportEnabled');
        onChange({
            ...crmModule,
            isImportDisabled: !changeData.event.currentTarget.checked,
        });
    }, [crmModule, onChange, onFieldChange]);

    return (
        <FormSection headline={CRM_NAMES[crmModule.crmType]}>
            <Group direction="v" alignItems="start">
                <div>
                    <Label>User ID</Label>
                    <CustomValidatedControl name={`user-${crmModule.crmType}-crm-id`}>
                        {error => (
                            <FormInput
                                readOnly={disabled}
                                width={300}
                                valid={error ? true : undefined}
                                errorDescription={error}
                                maxLength={CRM_MAX_LENGTH[crmModule.crmType]}
                                value={crmModule.crmId}
                                onBlur={changeUserId}
                            />
                        )}
                    </CustomValidatedControl>
                </div>
                <Group alignItems="center">
                    {crmModule.isAdmin !== null && crmModule.isAdmin !== undefined && (
                        <CheckBox
                            disabled={disabled}
                            label="Admin"
                            checked={crmModule.isAdmin}
                            onCheckBoxChange={changeAdmin}
                        />
                    )}
                    {crmModule.isImportDisabled !== null && crmModule.isImportDisabled !== undefined && (
                        <CheckBox
                            disabled={disabled}
                            label="Enable Contacts Import"
                            checked={!crmModule.isImportDisabled}
                            onCheckBoxChange={changeContactsImport}
                        />
                    )}
                </Group>
            </Group>
        </FormSection>
    );
};
